import { toast } from "react-toastify";
import { Errors } from "../../../constants/errors";
import { SocialPostStatus } from "../config";

const RECONNECT_REQUIRED_ERROR_CODES = new Set<number>([
  Errors.InvalidatedSession,
  Errors.ExpiredToken,
  Errors.IsDisconnected,
  Errors.InvalidUserRole,
  Errors.NotBusinessAccount,
  Errors.PermissionsMissing,
]);

const PROVIDER_LABELS: Record<string, string> = {
  facebook: "Facebook",
  instagram: "Instagram",
  twitter: "X (Twitter)",
  linkedin: "LinkedIn",
  linkedinbusiness: "LinkedIn Business",
  tiktok: "TikTok",
  youtube: "YouTube",
};

/** Providers whose API message is already user-facing (the rest return internal service names). */
const PROVIDERS_WITH_FRIENDLY_API_MESSAGE = new Set(["tiktok", "youtube"]);

export function normalizePublishProvider(provider: string | undefined | null) {
  const key = String(provider ?? "").trim().toLowerCase();
  return key === "instagramlogin" ? "instagram" : key;
}

export function publishProviderLabel(provider: string | undefined | null) {
  const key = normalizePublishProvider(provider);
  return PROVIDER_LABELS[key] ?? String(provider ?? "");
}

export function joinLabels(labels: string[], conjunction = "y") {
  if (labels.length <= 1) return labels.join("");
  return `${labels.slice(0, -1).join(", ")} ${conjunction} ${labels[labels.length - 1]}`;
}

export function isReconnectRequiredCode(code: unknown) {
  return typeof code === "number" && RECONNECT_REQUIRED_ERROR_CODES.has(code);
}

export type PublishFailure = {
  provider: string;
  code?: number;
  message?: string;
};

export function toPublishFailure(provider: string, error: any): PublishFailure {
  const data = error?.response?.data;
  let code: unknown = data?.code;
  let message: unknown = data?.message;
  if (code == null && typeof error?.request?.response === "string") {
    try {
      const parsed = JSON.parse(error.request.response);
      code = parsed?.code;
      message = message ?? parsed?.message;
    } catch {
      // Non-JSON body: keep the generic failure.
    }
  }
  const key = normalizePublishProvider(provider);
  return {
    provider: key,
    code: typeof code === "number" ? code : undefined,
    message:
      PROVIDERS_WITH_FRIENDLY_API_MESSAGE.has(key) &&
      typeof message === "string" &&
      message.trim()
        ? message.trim()
        : undefined,
  };
}

export function describePublishFailures(failures: PublishFailure[]) {
  const reconnect = failures.filter((f) => isReconnectRequiredCode(f.code));
  const other = failures.filter((f) => !isReconnectRequiredCode(f.code));
  const parts: string[] = [];
  if (reconnect.length) {
    parts.push(
      `La conexión con ${joinLabels(
        reconnect.map((f) => publishProviderLabel(f.provider))
      )} expiró: reconectá la cuenta en Integraciones.`
    );
  }
  other
    .filter((f) => f.message)
    .forEach((f) => parts.push(`${publishProviderLabel(f.provider)}: ${f.message}`));
  if (!parts.length || other.some((f) => !f.message)) {
    parts.push("Probá de nuevo en unos minutos.");
  }
  return { text: parts.join(" "), needsReconnect: reconnect.length > 0 };
}

const PENDING_IMMEDIATE_TTL_MS = 15 * 60 * 1000;
const pendingImmediatePublishes = new Map<string, number[]>();

/**
 * "Publicar ahora" responde apenas el backend recibe el post; el resultado real llega
 * después por socket. Solo mostramos ese resultado en la pestaña que publicó.
 */
export function registerPendingImmediatePublish(providers: string[]) {
  const now = Date.now();
  providers.forEach((provider) => {
    const key = normalizePublishProvider(provider);
    const list = pendingImmediatePublishes.get(key) ?? [];
    list.push(now);
    pendingImmediatePublishes.set(key, list);
  });
}

export function consumePendingImmediatePublish(provider: string) {
  const key = normalizePublishProvider(provider);
  const now = Date.now();
  const list = (pendingImmediatePublishes.get(key) ?? []).filter(
    (ts) => now - ts < PENDING_IMMEDIATE_TTL_MS
  );
  const found = list.length > 0;
  if (found) list.shift();
  if (list.length) pendingImmediatePublishes.set(key, list);
  else pendingImmediatePublishes.delete(key);
  return found;
}

export type SocialPostResultNotification = {
  social?: string;
  status?: number;
  errorCode?: number;
  immediate?: boolean;
};

export function showImmediatePublishResult(
  data: SocialPostResultNotification | undefined,
  onReconnect: () => void
) {
  if (!data?.immediate || !data.social) return;
  if (
    data.status !== SocialPostStatus.Publish &&
    data.status !== SocialPostStatus.Failed
  ) {
    return;
  }
  if (!consumePendingImmediatePublish(data.social)) return;

  const label = publishProviderLabel(data.social);
  if (data.status === SocialPostStatus.Publish) {
    toast.success(`Listo: tu publicación ya está en ${label}.`, {
      autoClose: 6000,
    });
    return;
  }

  if (isReconnectRequiredCode(data.errorCode)) {
    toast.error(
      `No se pudo publicar en ${label}: la conexión expiró. Tocá acá para reconectar la cuenta en Integraciones.`,
      { autoClose: 12000, onClick: onReconnect }
    );
    return;
  }
  toast.error(
    `No se pudo publicar en ${label}. Revisá el detalle en el calendario y probá de nuevo.`,
    { autoClose: 10000 }
  );
}
